import { ApolloError } from '@apollo/client'
import { useErrorHandler } from 'components/error'
import { history } from 'config/history'
import { useMarcarVisualizadoLembreteProfissionalMutation } from 'graphql/hooks.generated'
import { useBlockInteractionsOutside } from 'hooks/useBlockInteractionsOutside'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react'
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride'
import { emptyArrayOf } from 'util/array'

import {
  AutoTourDefinition,
  ManagedTour,
  TourAction,
  TourControllerContextData,
  TourDefinition,
  tourPlacementRecord,
  TourState,
  TourStep,
  TourTooltipContextData,
} from '../types/model-tour'
import { cancelTourNavigationPrompt, registerTourNavigationPromptController } from './tourNavigationPrompt'
import { TourTooltip } from './TourTooltip'

const EMPTY_STEPS = emptyArrayOf<TourStep>()
const TOUR_SCROLL_OFFSET = 150
const TOUR_SCROLL_DURATION_IN_MILLISECONDS = 700
const JOYRIDE_STYLES = {
  options: {
    zIndex: 10000,
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    primaryColor: '#0069D9',
  },
}
const JOYRIDE_FLOATER_PROPS = {
  disableAnimation: true,
}

const INITIAL_STATE: TourState = {
  activeTour: null,
  queue: [],
  runKey: 0,
}

const tourReducer = (state: TourState, action: TourAction): TourState => {
  switch (action.type) {
    case 'START_TOUR': {
      if (!action.tour.steps.length) {
        return state
      }

      if (state.activeTour) {
        return {
          ...state,
          queue: [...state.queue, action.tour],
        }
      }

      return {
        activeTour: action.tour,
        queue: state.queue,
        runKey: state.runKey + 1,
      }
    }

    case 'FINISH_TOUR': {
      if (!state.activeTour || state.runKey !== action.runKey) {
        return state
      }

      const [nextTour, ...remainingQueue] = state.queue

      return {
        activeTour: nextTour ?? null,
        queue: remainingQueue,
        runKey: nextTour ? state.runKey + 1 : state.runKey,
      }
    }

    case 'STOP_TOUR': {
      if (!state.activeTour && !state.queue.length) {
        return state
      }

      return {
        ...state,
        activeTour: null,
        queue: [],
      }
    }

    default:
      return state
  }
}

const TourContext = createContext<TourControllerContextData | null>(null)
const TourTooltipContext = createContext<TourTooltipContextData | null>(null)
type PersistedRunStatus = 'idle' | 'pending' | 'success' | 'failed'

export const useTourTooltipRefContext = () => {
  const context = useContext(TourTooltipContext)

  if (!context) {
    throw new Error('useTourTooltipRefContext deve ser usado dentro de um TourProvider')
  }

  return context
}

const mapTourStepsToJoyrideSteps = (steps: ReadonlyArray<TourStep>): Step[] =>
  steps.map(({ target, content, title, placement, spotlightPadding }) => ({
    target,
    content,
    title,
    placement: placement !== undefined ? tourPlacementRecord[placement] : undefined,
    disableBeacon: true,
    ...(spotlightPadding !== undefined && { spotlightPadding }),
  }))

const isFinishedJoyrideStatus = (status: CallBackProps['status']) =>
  status === STATUS.FINISHED || status === STATUS.SKIPPED

export function TourProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [state, dispatch] = useReducer(tourReducer, INITIAL_STATE)
  const lastFinishedRunKeyRef = useRef<number | null>(null)
  const persistedViewedRunStatusRef = useRef<Map<number, PersistedRunStatus>>(new Map())
  const activeTourRef = useRef<ManagedTour | null>(null)
  const runKeyRef = useRef<number>(INITIAL_STATE.runKey)
  const pendingNavigationConfirmationRef = useRef<{ tour: ManagedTour; runKey: number } | null>(null)
  const tooltipRef = useRef<HTMLElement | null>(null)
  const handleError = useErrorHandler()

  const [marcarVisualizadoMutation] = useMarcarVisualizadoLembreteProfissionalMutation()

  const persistAutoTourAsViewed = useCallback(
    async (tour: ManagedTour, runKey: number) => {
      if (tour.mode !== 'auto') {
        return
      }

      const status = persistedViewedRunStatusRef.current.get(runKey) ?? 'idle'
      if (status === 'pending' || status === 'success') {
        return
      }

      persistedViewedRunStatusRef.current.set(runKey, 'pending')

      try {
        const response = await marcarVisualizadoMutation({
          variables: {
            id: tour.lembreteId,
          },
          context: {
            fetchOptions: {
              keepalive: true,
            },
          },
        })
        persistedViewedRunStatusRef.current.set(runKey, 'success')
        return response
      } catch (error) {
        persistedViewedRunStatusRef.current.set(runKey, 'failed')
        handleError(error as ApolloError)
      }
    },
    [handleError, marcarVisualizadoMutation]
  )

  const queueManualTour = useCallback((tour: TourDefinition) => {
    dispatch({
      type: 'START_TOUR',
      tour: { ...tour, mode: 'manual' },
    })
  }, [])

  const queueAutoTour = useCallback((tour: AutoTourDefinition) => {
    dispatch({
      type: 'START_TOUR',
      tour: { ...tour, mode: 'auto' },
    })
  }, [])

  const startTour = useCallback(
    (tour: TourDefinition) => {
      queueManualTour(tour)
    },
    [queueManualTour]
  )

  const startAutoTour = useCallback(
    (tour: AutoTourDefinition) => {
      queueAutoTour(tour)
    },
    [queueAutoTour]
  )

  const finishCurrentTour = useCallback(
    (activeTour: ManagedTour, runKey: number) => {
      if (lastFinishedRunKeyRef.current === runKey) {
        return
      }

      lastFinishedRunKeyRef.current = runKey
      dispatch({ type: 'FINISH_TOUR', runKey })
      return persistAutoTourAsViewed(activeTour, runKey)
    },
    [persistAutoTourAsViewed]
  )

  const stopAndPersistCurrentTourAsViewed = useCallback(
    (activeTour: ManagedTour, runKey: number) => {
      if (lastFinishedRunKeyRef.current === runKey) {
        return
      }

      if (activeTour.mode !== 'auto') {
        dispatch({ type: 'STOP_TOUR' })
        return
      }

      lastFinishedRunKeyRef.current = runKey
      dispatch({ type: 'STOP_TOUR' })
      return persistAutoTourAsViewed(activeTour, runKey)
    },
    [persistAutoTourAsViewed]
  )

  const prepareActiveTourForNavigationPrompt = useCallback(() => {
    const activeTour = activeTourRef.current
    if (!activeTour) {
      return
    }

    if (activeTour.mode === 'auto') {
      pendingNavigationConfirmationRef.current = { tour: activeTour, runKey: runKeyRef.current }
    } else {
      pendingNavigationConfirmationRef.current = null
    }

    dispatch({ type: 'STOP_TOUR' })
  }, [])

  const confirmNavigationPrompt = useCallback(async () => {
    const pendingConfirmation = pendingNavigationConfirmationRef.current
    pendingNavigationConfirmationRef.current = null

    if (!pendingConfirmation) {
      return
    }

    await persistAutoTourAsViewed(pendingConfirmation.tour, pendingConfirmation.runKey)
  }, [persistAutoTourAsViewed])

  const cancelNavigationPrompt = useCallback(() => {
    pendingNavigationConfirmationRef.current = null
  }, [])

  const handleCallback = useCallback(
    ({ status }: CallBackProps) => {
      if (state.activeTour && isFinishedJoyrideStatus(status)) {
        finishCurrentTour(state.activeTour, state.runKey)
      }
    },
    [finishCurrentTour, state.activeTour, state.runKey]
  )

  const stopTour = useCallback(() => {
    dispatch({ type: 'STOP_TOUR' })
  }, [])

  const setTooltipNode = useCallback((node: HTMLElement | null) => {
    tooltipRef.current = node
  }, [])

  const contextValue = useMemo(
    () => ({
      startTour,
      startAutoTour,
      stopTour,
    }),
    [startAutoTour, startTour, stopTour]
  )
  const tooltipRefContextValue = useMemo<TourTooltipContextData>(
    () => ({
      tooltipRef,
      setTooltipNode,
    }),
    [setTooltipNode]
  )
  const joyrideSteps = useMemo(() => mapTourStepsToJoyrideSteps(state.activeTour?.steps ?? EMPTY_STEPS), [
    state.activeTour,
  ])

  useBlockInteractionsOutside({
    block: !!state.activeTour,
    allowedRef: tooltipRef,
  })

  useEffect(() => {
    activeTourRef.current = state.activeTour
    runKeyRef.current = state.runKey
  }, [state.activeTour, state.runKey])

  useEffect(() => {
    if (!state.activeTour || state.activeTour.mode !== 'auto') {
      return
    }

    persistAutoTourAsViewed(state.activeTour, state.runKey)
  }, [state.activeTour, state.runKey, persistAutoTourAsViewed])

  useEffect(() => {
    if (!state.activeTour) {
      return
    }

    const activeTour = state.activeTour
    const runKey = state.runKey

    const unlisten = history.listen(() => {
      stopAndPersistCurrentTourAsViewed(activeTour, runKey)
    })

    return () => {
      if (typeof unlisten === 'function') {
        unlisten()
      }
    }
  }, [state.activeTour, state.runKey, stopAndPersistCurrentTourAsViewed])

  useEffect(() => {
    if (!state.activeTour || state.activeTour.mode !== 'auto') {
      return
    }

    const activeTour = state.activeTour
    const runKey = state.runKey
    const onBeforeUnload = () => {
      stopAndPersistCurrentTourAsViewed(activeTour, runKey)
    }

    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [state.activeTour, state.runKey, stopAndPersistCurrentTourAsViewed])

  useEffect(
    () =>
      registerTourNavigationPromptController({
        hasActiveTour: () => !!activeTourRef.current,
        prepare: prepareActiveTourForNavigationPrompt,
        confirm: confirmNavigationPrompt,
        cancel: cancelNavigationPrompt,
      }),
    [cancelNavigationPrompt, confirmNavigationPrompt, prepareActiveTourForNavigationPrompt]
  )

  useEffect(() => cancelTourNavigationPrompt, [])

  return (
    <TourContext.Provider value={contextValue}>
      <TourTooltipContext.Provider value={tooltipRefContextValue}>
        <Joyride
          key={state.runKey}
          steps={joyrideSteps}
          run={Boolean(state.activeTour)}
          continuous
          floaterProps={JOYRIDE_FLOATER_PROPS}
          scrollToFirstStep
          showProgress
          showSkipButton
          disableOverlayClose
          disableCloseOnEsc
          spotlightClicks={false}
          disableScrollParentFix={false}
          tooltipComponent={TourTooltip}
          callback={handleCallback}
          scrollOffset={TOUR_SCROLL_OFFSET}
          scrollDuration={TOUR_SCROLL_DURATION_IN_MILLISECONDS}
          styles={JOYRIDE_STYLES}
        />
        {children}
      </TourTooltipContext.Provider>
    </TourContext.Provider>
  )
}

export const useTourContext = () => {
  const context = useContext(TourContext)

  if (!context) {
    throw new Error('useTourContext deve ser usado dentro de um TourProvider')
  }

  return context
}
